import React from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PageHeader, TenantSelectFilterBlock } from '@/shared/components';
import { Switch } from '@/lib/shadcn/components/ui';
import { useScopedTenantFilter } from '@/shared/hooks/useTenantFilter';
import { useIsSuperAdmin } from '@/shared/hooks/useIsSuperAdmin';
import { useCustomersManager } from '../managers';
import { CustomerDataTable } from '../components/CustomerDataTable';
import { BexioContactMigrationButton } from '../components/BexioContactMigration';
import { ConnectionStatus } from '../constants';
import { useRouter } from '@tanstack/react-router';
import type { FilterValues } from '@/shared/components/DataTable';

export const CustomersListPage: React.FC = () => {
  const { t } = useTranslation('customers');
  const { navigate } = useRouter();
  const { effectiveTenantId, setTenant } = useScopedTenantFilter('customers');
  const isSuperAdmin = useIsSuperAdmin();

  const {
    customers,
    pagination,
    loading,
    currentFilters,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    handleFilterChange,
  } = useCustomersManager(
    {},
    isSuperAdmin ? effectiveTenantId : null
  );

  const showDisconnectedDevices =
    currentFilters.connectionStatus === ConnectionStatus.DISCONNECTED;

  const gotoCreateCustomer = () => {
    navigate({ to: '/customers/create' });
  };

  const handleDisconnectedDevicesToggle = (checked: boolean) => {
    const next: FilterValues = { ...currentFilters };
    if (checked) {
      next.connectionStatus = ConnectionStatus.DISCONNECTED;
    } else {
      delete next.connectionStatus;
    }
    handleFilterChange(next);
  };

  const tenantFilterActive = isSuperAdmin && effectiveTenantId != null;

  return (
    <div className="container mx-auto py-6 space-y-6">
      <PageHeader
        title={t('list.title')}
        description={t('list.subtitle')}
        action={{
          label: t('list.createCustomer'),
          onClick: gotoCreateCustomer,
          icon: <Plus className="mr-2 h-4 w-4" />,
        }}
      />

      <CustomerDataTable
        customers={customers ?? []}
        onClickCustomer={(customer) => {
          navigate({ to: '/customers/$id', params: { id: customer.Id } });
        }}
        loading={loading}
        pagination={pagination}
        currentFilters={currentFilters}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSortChange={handleSortChange}
        onFilterChange={handleFilterChange}
        filterToolbarSlot={
          <>
            {isSuperAdmin ? (
              <TenantSelectFilterBlock
                value={effectiveTenantId}
                onChange={setTenant}
              />
            ) : null}
            <div className="basis-full w-full order-last flex items-center gap-2">
              <Switch
                checked={showDisconnectedDevices}
                onCheckedChange={handleDisconnectedDevicesToggle}
                aria-label={t('list.filtersLabels.showDisconnectedDevices')}
              />
              <span className="text-sm font-medium text-foreground">
                {t('list.filtersLabels.showDisconnectedDevices')}
              </span>
            </div>
          </>
        }
        filterToolbarTrailingSlot={
          isSuperAdmin ? <BexioContactMigrationButton /> : undefined
        }
        filterToolbarActiveCount={
          (tenantFilterActive ? 1 : 0) + (showDisconnectedDevices ? 1 : 0)
        }
        onClearFilterToolbar={
          isSuperAdmin ? () => setTenant(null) : undefined
        }
      />
    </div>
  );
};
